import { useColorScheme, useTheme } from "@mui/material";
import * as monaco from "monaco-editor";
import { useEffect, useMemo, useRef, useState } from "react";
import { getSbsTheme } from "../components/MonacoEditor/utils";
import { KEYWORDS, TYPES } from "../lib/SBSEditorConfig/constants";

type DiffEditorOptions = {
  options?: monaco.editor.IDiffEditorOptions;
  onDidUpdateDiff?: (editor: monaco.editor.IDiffEditor | null) => void;
  onPositionUpdate?: (e: monaco.editor.ICursorPositionChangedEvent, editor: monaco.editor.ICodeEditor) => void;
};

function useDiffEditor({ onDidUpdateDiff, onPositionUpdate, options = {} }: DiffEditorOptions) {
  const editorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);
  const monacoEl = useRef<HTMLDivElement | null>(null);
  const { mode, systemMode } = useColorScheme();
  const theme = useTheme();
  const [isEditorReady, setIsEditorReady] = useState<boolean>(false);

  const vsTheme = mode === "dark" || systemMode === "dark" ? "vs-dark" : "vs";

  useEffect(() => {
    monaco.editor.setTheme(vsTheme);
  }, [vsTheme]);

  const disposeEditor = () => {
    const models = editorRef.current?.getModel();

    models?.original?.dispose();
    models?.modified.dispose();

    editorRef.current?.dispose();
  };

  useEffect(() => {
    if (monacoEl && monacoEl.current) {
      // Apply SBS config
      monaco.languages.register({ id: "sbs" });

      monaco.languages.setMonarchTokensProvider("sbs", {
        keywords: [...KEYWORDS, "import", "export"],
        types: TYPES,
        tokenizer: {
          root: [
            [
              /@?[a-zA-Z][\w$]*/,
              {
                cases: {
                  "@keywords": "keyword",
                  "@types": "type",
                  "@default": "variable",
                },
              },
            ],
            [/\b\/\*/, "comment", "@comment"], // Multi-line comments start
            [/\/\/.*/, "comment"], // Single-line comments start
            [/\/\*/, "comment", "@comment"], // Nested multi-line comments start
            [/\/\*/, "comment.invalid"], // Malformed multi-line comments
            [/\/\*/, "comment", "@comment"], // Properly nested multi-line comments
            [/\/\/.*/, "comment"], // Single-line comments can follow multi-line
            [/.*\*\//, "comment", "@comment"],
          ],
          comment: [
            [/\*\//, "comment", "@pop"],
            [/[^*]+/, "comment"],
            [/\*/, "comment"],
          ],
        },
      });

      monaco.editor.defineTheme("sbs-theme", getSbsTheme(theme, vsTheme));

      editorRef.current = monaco.editor.createDiffEditor(monacoEl.current, {
        theme: "sbs-theme",
        enableSplitViewResizing: false,
        renderSideBySide: false,
        automaticLayout: true,
        diffCodeLens: true,
        diffAlgorithm: "advanced",
        // See https://github.com/microsoft/monaco-editor/issues/5059
        editContext: typeof window !== "undefined" && window.Cypress ? false : undefined,
        ...options,
      });

      editorRef.current.onDidUpdateDiff(() => {
        if (onDidUpdateDiff) {
          onDidUpdateDiff(editorRef.current);
        }
      });

      setIsEditorReady(true);
    }

    return () => disposeEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is subtle to fix
  }, []);

  useEffect(() => {
    const modifiedEditor = editorRef.current?.getModifiedEditor();
    if (onPositionUpdate && modifiedEditor) {
      modifiedEditor.onDidChangeCursorPosition((e) => onPositionUpdate(e, modifiedEditor));
    }
  }, [onPositionUpdate]);

  return useMemo(
    () => ({
      editorRef,
      monacoEl,
      isEditorReady,
    }),
    [editorRef, monacoEl, isEditorReady]
  );
}

export default useDiffEditor;
